import { Alert, Box, Button, TextField, Typography } from "@mui/material";
import { COLORS } from "assets";
import { BREAK_GLASS_APPROVED_EVENT, BREAK_GLASS_EVENT, ENDPOINTS } from "config/constants";
import { useContextController } from "context";
import { useFetch } from "hooks";
import useTranslation from "hooks/useTranslation";
import { useSnackbar } from "notistack";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  getApprovedBreakGlassSiteIds,
  setApprovedBreakGlassSiteIds,
  subscribeBreakGlassApproved
} from "utils/breakGlass";
import FormModalActions from "./FormModalActions";
import MyModal from "./MyModal";
import SiteSelect from "./SiteSelect";

interface ActivateResponse {
  siteId?: string;
  siteName?: string;
  approvedSites?: string[];
}

interface ListResponse {
  siteIds?: string[];
  sites?: Array<{ _id: string; name?: string }>;
}

/**
 * Global for super-admins (mounted in UserLayout).
 * Opens when code dispatches BREAK_GLASS_EVENT (optionally with detail.siteId).
 * Access lasts for this browser session (until logout). No persistent page banner.
 */
export default function BreakGlassModal() {
  const [{ userData }] = useContextController();
  const t = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const [open, setOpen] = useState(false);
  const [siteId, setSiteId] = useState("");
  const [reason, setReason] = useState("");
  const [approvedIds, setApprovedIds] = useState<string[]>(() => getApprovedBreakGlassSiteIds());
  const [remainingCount, setRemainingCount] = useState<number | null>(null);
  const { isLoading, callFetch } = useFetch<ActivateResponse>("POST");
  const { callFetch: callFetchList } = useFetch<ListResponse>("GET");

  const refreshApproved = useCallback(() => {
    if (userData?.type !== "super") return;
    callFetchList(ENDPOINTS.breakGlass)
      .then((res) => {
        const ids = (res.data?.siteIds || []).map(String);
        setApprovedBreakGlassSiteIds(ids);
        setApprovedIds(ids);
      })
      .catch(() => undefined);
  }, [callFetchList, userData?.type]);

  useEffect(() => {
    return subscribeBreakGlassApproved(() => setApprovedIds(getApprovedBreakGlassSiteIds()));
  }, []);

  useEffect(() => {
    if (userData?.type !== "super") return;
    const openModal = (event: Event) => {
      const detail = (event as CustomEvent<{ siteId?: string }>).detail || {};
      const preferred = detail.siteId ? String(detail.siteId) : "";
      if (preferred && !getApprovedBreakGlassSiteIds().includes(preferred)) {
        setSiteId(preferred);
      } else {
        setSiteId("");
      }
      setReason("");
      setRemainingCount(null);
      setOpen(true);
    };
    window.addEventListener(BREAK_GLASS_EVENT, openModal as EventListener);
    refreshApproved();
    return () => window.removeEventListener(BREAK_GLASS_EVENT, openModal as EventListener);
  }, [userData?.type, refreshApproved]);

  const excludeIds = useMemo(() => approvedIds, [approvedIds]);
  const allUnlocked = remainingCount === 0 && !siteId;

  if (userData?.type !== "super") return null;

  const submit = () => {
    const trimmed = reason.trim();
    if (!siteId) {
      enqueueSnackbar("Select a site first.", { variant: "warning" });
      return;
    }
    if (trimmed.length < 10) {
      enqueueSnackbar("Enter a reason of at least 10 characters.", { variant: "warning" });
      return;
    }
    callFetch(ENDPOINTS.breakGlass, {
      body: JSON.stringify({ siteId, reason: trimmed })
    })
      .then((res) => {
        const unlocked = String(res.data?.siteId || siteId);
        const nextIds = Array.from(
          new Set([...(res.data?.approvedSites || getApprovedBreakGlassSiteIds()).map(String), unlocked])
        );
        setApprovedBreakGlassSiteIds(nextIds);
        setApprovedIds(nextIds);
        enqueueSnackbar(res.message || "Break-glass granted for this site until you log out.", {
          variant: "success"
        });
        setOpen(false);
        setReason("");
        setSiteId("");
        refreshApproved();
        window.dispatchEvent(
          new CustomEvent(BREAK_GLASS_APPROVED_EVENT, { detail: { siteId: unlocked } })
        );
      })
      .catch((err) => {
        enqueueSnackbar(err?.message || "Could not activate break-glass.", { variant: "error" });
      });
  };

  return (
    <MyModal
      title={t["Break-glass access"] || "Break-glass access"}
      subtitle={
        t["Break-glass modal subtitle"] ||
        "Required for super admins before viewing a home’s boxes, cameras, alerts, or media. Unlock lasts for this browser session (until logout) and is audited."
      }
      open={open}
      setOpen={setOpen}
      variant="light"
      width={480}
    >
      <Box display="flex" flexDirection="column" gap={2}>
        {allUnlocked ? (
          <>
            <Alert severity="success">All sites are unlocked for this session (until logout).</Alert>
            <Box display="flex" justifyContent="flex-end" mt={1}>
              <Button
                variant="outlined"
                onClick={() => setOpen(false)}
                sx={{
                  minWidth: 120,
                  height: 44,
                  borderRadius: `${COLORS.radius.md}px`,
                  borderColor: COLORS.surface.border,
                  color: COLORS.surface.primaryText,
                  textTransform: "none"
                }}
              >
                Close
              </Button>
            </Box>
          </>
        ) : (
          <>
            <Alert severity="info">
              Required before viewing that site&apos;s boxes, cameras, alerts, incidents, or media. Sites you
              already unlocked this session are not listed.
            </Alert>
            {approvedIds.length > 0 ? (
              <Typography variant="body2" color="text.secondary">
                {approvedIds.length} site{approvedIds.length === 1 ? "" : "s"} unlocked this session · lasts until
                logout
              </Typography>
            ) : null}
            <SiteSelect
              value={siteId || undefined}
              onChange={setSiteId}
              hideLabel={false}
              excludeIds={excludeIds}
              disableAutoSelect
              onSites={(sites) => setRemainingCount(sites.length)}
            />
            <TextField
              label="Reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              multiline
              minRows={3}
              fullWidth
              placeholder="Support ticket #… / outage investigation…"
              helperText="Minimum 10 characters · Access lasts until logout"
            />
            <FormModalActions
              onCancel={() => setOpen(false)}
              cancelLabel="Cancel"
              submitLabel="Unlock site"
              submitType="button"
              onSubmit={submit}
              loading={isLoading}
            />
          </>
        )}
      </Box>
    </MyModal>
  );
}
